import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import Modal from 'components/Modal';
import CSVReader from 'react-csv-reader';
import {
  BulkImportFromExternalProviderDto,
  BulkImportPreviewRowDto,
  BulkImportRowAction,
  ExternalReceiptProvider,
} from 'api/model';
import moment from 'moment-timezone';
import {
  useReceiptBulkImportFromExternalProvider,
  useReceiptPreviewBulkImportFromExternalProvider,
} from 'api/receipt/receipt';
import Table from 'components/Table';
import { formatDateTime } from 'utils/date-helpers';
import {
  makeDateComparator,
  makeNumberComparator,
  makeStringComparator,
} from 'utils/sorting';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import useTranslations from 'contexts/basicData/useTranslations';
import Routes from 'constants/Routes';

const FormWrap = styled.div<{ maxWidth?: number; compact?: boolean }>`
  margin-top: 10px;
  padding-right: 10px;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ compact }) => (compact ? '0' : '10px')};
  ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth}px;` : '')}
`;

const ReviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(1300px, 85vw);
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

type Tone = 'good' | 'neutral' | 'warning';

const SummaryChip = styled.button<{ selected: boolean; tone: Tone }>`
  ${({ selected, tone, theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    background: ${selected
      ? theme.colors.background.selection
      : theme.colors.background.secondary};
    border: 1px solid
      ${selected ? theme.colors.foreground.tint : theme.colors.border.light};
    color: ${theme.colors.foreground.primary};

    strong {
      font-size: 18px;
      color: ${selected
        ? theme.colors.foreground.primary
        : {
            good: theme.colors.foreground.good,
            neutral: theme.colors.foreground.primary,
            warning: theme.colors.foreground.warning,
          }[tone]};
    }
  `}
`;

const Hint = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  white-space: normal;
`;

const Note = styled.span<{ tone?: Tone }>`
  font-size: 12px;
  color: ${({ tone, theme }) =>
    tone === 'warning'
      ? theme.colors.foreground.warning
      : theme.colors.foreground.secondary};
`;

const ActionLabel = styled.span<{ tone: Tone }>`
  font-weight: 600;
  color: ${({ tone, theme }) =>
    ({
      good: theme.colors.foreground.good,
      neutral: theme.colors.foreground.primary,
      warning: theme.colors.foreground.warning,
    })[tone]};
`;

const actionLabels: Record<BulkImportRowAction, { label: string; tone: Tone }> =
  {
    [BulkImportRowAction.New]: { label: 'Läggs till', tone: 'good' },
    [BulkImportRowAction.AddedToExistingReceipt]: {
      label: 'Läggs till på befintligt köp',
      tone: 'good',
    },
    [BulkImportRowAction.UpdatesExisting]: {
      label: 'Uppdateras',
      tone: 'good',
    },
    [BulkImportRowAction.AlreadyImported]: {
      label: 'Finns redan – hoppas över',
      tone: 'neutral',
    },
    [BulkImportRowAction.DuplicateInFile]: {
      label: 'Dubblett i filen – hoppas över',
      tone: 'warning',
    },
    [BulkImportRowAction.AlreadyImportedNotConnected]: {
      label: 'Finns redan, inte kopplad',
      tone: 'neutral',
    },
  };

const isSkipped = (preview: BulkImportPreviewRowDto) =>
  preview.action === BulkImportRowAction.AlreadyImported ||
  preview.action === BulkImportRowAction.DuplicateInFile;

const isNewOrChanged = (preview: BulkImportPreviewRowDto) =>
  preview.action === BulkImportRowAction.New ||
  preview.action === BulkImportRowAction.AddedToExistingReceipt ||
  preview.action === BulkImportRowAction.UpdatesExisting;

type Filter =
  | 'all'
  | 'imported'
  | 'existing'
  | 'duplicates'
  | 'connects'
  | 'notConnected';

const filters: Record<
  Filter,
  { label: string; tone: Tone; matches(p: BulkImportPreviewRowDto): boolean }
> = {
  all: { label: 'Rader i filen', tone: 'neutral', matches: () => true },
  imported: {
    label: 'Nya eller ändrade',
    tone: 'good',
    matches: isNewOrChanged,
  },
  existing: {
    label: 'Finns redan',
    tone: 'neutral',
    matches: (p) =>
      p.action === BulkImportRowAction.AlreadyImported ||
      p.action === BulkImportRowAction.AlreadyImportedNotConnected,
  },
  duplicates: {
    label: 'Dubbletter i filen',
    tone: 'warning',
    matches: (p) => p.action === BulkImportRowAction.DuplicateInFile,
  },
  connects: {
    label: 'Kopplas till uppdrag',
    tone: 'good',
    matches: (p) => p.willConnect,
  },
  notConnected: {
    label: 'Kopplas inte',
    tone: 'warning',
    matches: (p) => !isSkipped(p) && !p.willConnect,
  },
};

// Kolumnerna importen läser ur Circle Ks export. Beloppet tas från bruttokolumnen och faller
// tillbaka på Total; kvittonumret används inte för att känna igen köp och får saknas.
const requiredColumns = [
  'Transaction ID',
  'Date',
  'Card',
  'Station',
  'Product',
  'Volume',
  'Unit',
];
const amountColumns = ['Gross amount, purchase currency', 'Total'];

const findMissingColumns = (headers: string[]) => {
  const missing = requiredColumns.filter((c) => !headers.includes(c));
  if (!amountColumns.some((c) => headers.includes(c))) {
    missing.push(amountColumns[0]);
  }
  return missing;
};

type ReviewRow = {
  index: number;
  row: BulkImportFromExternalProviderDto;
  preview: BulkImportPreviewRowDto;
};

type Props = {
  onCancel: () => void;
  onSaved: () => void;
};
const UploadCsvModal: React.FC<Props> = ({ onCancel, onSaved }) => {
  const [file, setFile] = useState<BulkImportFromExternalProviderDto[]>([]);
  const [preview, setPreview] = useState<BulkImportPreviewRowDto[]>();
  const [filter, setFilter] = useState<Filter>('all');
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const [missingColumns, setMissingColumns] = useState<string[]>([]);
  const { expenseTypes } = useTranslations();

  const handleFilePicked = (data: any) => {
    const headers: string[] = (data[0] ?? []).map((h: any) =>
      `${h ?? ''}`.replace(/^\uFEFF/, '').trim()
    );
    const missing = findMissingColumns(headers);
    setMissingColumns(missing);
    setPreview(undefined);
    setFilter('all');
    if (missing.length > 0) {
      setFile([]);
      return;
    }
    const dateIndex = headers.indexOf('Date');
    const transactionIdIndex = headers.indexOf('Transaction ID');
    const cardIndex = headers.indexOf('Card');
    const stationIndex = headers.indexOf('Station');
    const receiptIndex = headers.indexOf('Receipt');
    const productIndex = headers.indexOf('Product');
    const volumeIndex = headers.indexOf('Volume');
    // Bruttobeloppet (före rabatt) — samma belopp som Circle Ks pushnotiser ger.
    // Äldre exporter utan kolumnen faller tillbaka på Total.
    const grossIndex = headers.indexOf('Gross amount, purchase currency');
    const totalIndex = grossIndex >= 0 ? grossIndex : headers.indexOf('Total');
    const unitIndex = headers.indexOf('Unit');
    const formattedData = data
      .slice(1)
      // Exporten slutar ofta med en tom rad.
      .filter((row: any) => row.some((cell: any) => `${cell ?? ''}`.trim()))
      .map((row: any) => {
        return {
          // Exporten har svensk lokaltid; tolka den så oavsett webbläsarens tidszon.
          date: moment
            .tz(row[dateIndex], 'DD.MM.YYYY HH:mm', 'Europe/Stockholm')
            .toDate(),
          // skipping partnerName
          // skipping cardGroupType
          transactionId: row[transactionIdIndex],
          card: row[cardIndex],
          // skipping cardTextLine2
          station: row[stationIndex],
          receipt: row[receiptIndex],
          // skipping country
          product: row[productIndex],
          volume: row[volumeIndex],
          total: row[totalIndex],
          unit: row[unitIndex],
          // skipping status
        };
      });
    setFile(formattedData);
  };

  const previewCall = useReceiptPreviewBulkImportFromExternalProvider();
  const uploadCall = useReceiptBulkImportFromExternalProvider();

  const handleReview = () => {
    if (!file.length) {
      return;
    }
    // Ett fel visas via previewCall.isError under filväljaren.
    previewCall.mutate(
      { data: file, params: { provider: ExternalReceiptProvider.CircleK } },
      { onSuccess: setPreview }
    );
  };

  const reviewRows: ReviewRow[] = useMemo(
    () =>
      (preview ?? []).map((p) => ({
        index: p.rowIndex,
        row: file[p.rowIndex],
        preview: p,
      })),
    [preview, file]
  );

  const counts = useMemo(
    () =>
      Object.fromEntries(
        (Object.keys(filters) as Filter[]).map((key) => [
          key,
          reviewRows.filter((r) => filters[key].matches(r.preview)).length,
        ])
      ) as Record<Filter, number>,
    [reviewRows]
  );

  // Nya eller ändrade rader, eller befintliga som nu kan kopplas.
  const hasWork = counts.imported > 0 || counts.connects > 0;

  const handleConfirmUpload = async () => {
    if (!file.length) {
      return;
    }

    // Hela filen skickas, även raderna som hoppas över: backenden räknar ett köps
    // produktrader i uppladdningen, och utfallet ska bli det som granskningen visade.
    // Split the file into chunks of about 50 rows. All product rows of one purchase
    // ("SE-687509846130-00001", "-00002") go in the same chunk: the backend leaves a
    // receipt that already exists untouched, so a purchase split across chunks would
    // lose its later rows.
    const purchaseKey = (row: BulkImportFromExternalProviderDto) =>
      row.transactionId?.split('-').slice(0, 2).join('-') || '';
    const purchases = new Map<string, BulkImportFromExternalProviderDto[]>();
    file.forEach((row, index) => {
      const key = purchaseKey(row) || `row-${index}`;
      purchases.set(key, [...(purchases.get(key) ?? []), row]);
    });
    const chunkSize = 50;
    const chunkedFile: BulkImportFromExternalProviderDto[][] = [];
    let currentChunk: BulkImportFromExternalProviderDto[] = [];
    purchases.forEach((rows) => {
      if (
        currentChunk.length > 0 &&
        currentChunk.length + rows.length > chunkSize
      ) {
        chunkedFile.push(currentChunk);
        currentChunk = [];
      }
      currentChunk = [...currentChunk, ...rows];
    });
    if (currentChunk.length > 0) {
      chunkedFile.push(currentChunk);
    }

    // Upload each chunk sequentially
    let i = 0;
    for (const chunk of chunkedFile) {
      i += 1;
      const percentageOfTotal = (i / chunkedFile.length) * 100;
      setUploadMessage(`Importerar (${percentageOfTotal.toFixed(0)}%)`);
      // eslint-disable-next-line no-await-in-loop
      await uploadCall.mutateAsync({
        data: chunk, // Upload the current chunk
        params: {
          provider: ExternalReceiptProvider.CircleK,
        },
      });
    }

    setUploadMessage('');

    onSaved();
  };

  const renderReview = () => (
    <>
      <Summary>
        {(Object.keys(filters) as Filter[]).map((key) => (
          <SummaryChip
            key={key}
            onClick={() => setFilter(key)}
            selected={filter === key}
            tone={filters[key].tone}
            type="button"
          >
            <strong>{counts[key]}</strong>
            <span>{filters[key].label}</span>
          </SummaryChip>
        ))}
      </Summary>
      <Hint>
        {hasWork
          ? 'Inget har sparats än. Kontrollera raderna och klicka på “Bekräfta import”. Rader som kopplas får ett utlägg på uppdraget; de som inte kopplas hamnar i listan och kan kopplas för hand.'
          : 'Filen innehåller inget nytt och inga rader som kan kopplas. Inget behöver importeras.'}
      </Hint>
      <Table
        useColumnWidthAsFlexAmount
        rows={reviewRows.filter((r) => filters[filter].matches(r.preview))}
        columnSettings={[
          {
            head: 'Nr',
            render: (r) => r.index + 1,
            width: 50,
            sortFunction: makeNumberComparator((r) => r.index),
          },
          {
            head: 'Datum',
            render: (r) => formatDateTime(r.row.date),
            width: 130,
            sortFunction: makeDateComparator((r) => r.row.date),
          },
          {
            head: 'Kortinnehavare',
            render: (r) => (
              <Cell>
                <span>{r.preview.cardHolderName ?? '–'}</span>
                <Note>{r.row.card}</Note>
              </Cell>
            ),
            width: 170,
            sortFunction: makeStringComparator(
              (r) => r.preview.cardHolderName ?? ''
            ),
          },
          {
            head: 'Köp',
            render: (r) => (
              <Cell>
                <span>
                  {r.row.product} · {r.row.total} kr
                </span>
                <Note>
                  {r.row.station} · {r.row.transactionId}
                </Note>
              </Cell>
            ),
            width: 260,
            sortFunction: makeStringComparator((r) => r.row.product),
          },
          {
            head: 'Import',
            render: (r) => (
              <Cell>
                <ActionLabel tone={actionLabels[r.preview.action].tone}>
                  {actionLabels[r.preview.action].label}
                </ActionLabel>
                {r.preview.actionNote && <Note>{r.preview.actionNote}</Note>}
              </Cell>
            ),
            width: 220,
            sortFunction: makeNumberComparator((r) => r.preview.action),
          },
          {
            head: 'Uppdrag',
            render: (r) => {
              const { preview: p } = r;
              const assignment = p.assignmentId && p.caseId && (
                <a
                  href={`${Routes.search.index}/${p.caseId}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  Uppdrag {p.assignmentId}
                  {p.registrationNumber ? ` · ${p.registrationNumber}` : ''}
                </a>
              );
              if (p.willConnect) {
                return (
                  <Cell>
                    <ActionLabel tone="good">Kopplas</ActionLabel>
                    {assignment}
                    {p.expenseType !== undefined && (
                      <Note>{expenseTypes[Number(p.expenseType)]}</Note>
                    )}
                    {p.connectionNote && (
                      <Note tone="warning">{p.connectionNote}</Note>
                    )}
                  </Cell>
                );
              }
              if (isSkipped(p)) {
                return (
                  <Cell>
                    {p.assignmentId ? (
                      <span>Uppdrag {p.assignmentId}</span>
                    ) : null}
                    {p.connectionNote && <Note>{p.connectionNote}</Note>}
                  </Cell>
                );
              }
              return (
                <Cell>
                  <ActionLabel tone="warning">Kopplas inte</ActionLabel>
                  {p.connectionNote && <Note>{p.connectionNote}</Note>}
                </Cell>
              );
            },
            width: 300,
            sortFunction: makeStringComparator((r) =>
              r.preview.willConnect ? '0' : (r.preview.connectionNote ?? '1')
            ),
          },
        ]}
      />
    </>
  );

  const renderFilePreview = () => (
    <Table
      rows={file}
      columnSettings={[
        {
          head: 'Nr',
          render: (_, props, focused, index) => index + 1,
          width: 50,
        },
        {
          head: 'Transaktion',
          render: (row) => row.transactionId,
          width: 150,
          sortFunction: makeStringComparator((row) => row.transactionId),
        },
        {
          head: 'Datum',
          render: (row) => formatDateTime(row.date),
          width: 150,
          sortFunction: makeDateComparator((row) => row.date),
        },
        {
          head: 'Kortnummer',
          render: (row) => row.card,
          width: 150,
          sortFunction: makeStringComparator((row) => row.card),
        },
        {
          head: 'Station',
          render: (row) => row.station,
          width: 150,
          sortFunction: makeStringComparator((row) => row.station),
        },
        {
          head: 'Kvitto',
          render: (row) => row.receipt,
          width: 150,
          sortFunction: makeStringComparator((row) => row.receipt ?? ''),
        },
        {
          head: 'Produkt',
          render: (row) => row.product,
          width: 150,
          sortFunction: makeStringComparator((row) => row.product),
        },
        {
          head: 'Volym',
          render: (row) => row.volume,
          width: 150,
          sortFunction: makeNumberComparator((row) => row.volume),
        },
        {
          head: 'Totalt',
          render: (row) => row.total,
          width: 150,
          sortFunction: makeNumberComparator((row) => row.total),
        },
        {
          head: 'Enhet',
          render: (row) => row.unit,
          width: 150,
          sortFunction: makeStringComparator((row) => row.unit),
        },
      ]}
    />
  );

  const busy = previewCall.isLoading || uploadCall.isLoading;

  const buttons = preview
    ? [
        {
          label: 'Bekräfta import',
          onClick: handleConfirmUpload,
          disabled: busy || !hasWork,
        },
        {
          label: 'Tillbaka',
          onClick: () => setPreview(undefined),
          disabled: busy,
        },
        { label: 'Avbryt', onClick: onCancel, disabled: busy },
      ]
    : [
        {
          label: 'Granska',
          onClick: handleReview,
          disabled: busy || file.length === 0,
        },
        { label: 'Avbryt', onClick: onCancel, disabled: busy },
      ];

  const renderBody = () => {
    if (uploadCall.isLoading) {
      return (
        <LoadingSpinner>
          <span>{uploadMessage}</span>
        </LoadingSpinner>
      );
    }
    if (previewCall.isLoading) {
      return (
        <LoadingSpinner>
          <span>Granskar {file.length} rader…</span>
        </LoadingSpinner>
      );
    }
    if (preview) {
      return <ReviewWrap>{renderReview()}</ReviewWrap>;
    }
    return (
      <>
        <p>
          Välj exporten från Circle Ks kundportal. Innan något sparas visas
          vilka rader som redan finns och vilket uppdrag varje ny rad kopplas
          till.
        </p>
        <FormWrap>
          <CSVReader
            cssClass="csv-input"
            inputId="csv-file-input"
            inputName="csv-file-input"
            onError={(_err) => {
              // console.log('error', err);
            }}
            onFileLoaded={handleFilePicked}
            parserOptions={{}}
          />
          {missingColumns.length > 0 && (
            <Note tone="warning">
              Filen saknar{' '}
              {missingColumns.length === 1 ? 'kolumnen' : 'kolumnerna'}{' '}
              {missingColumns.join(', ')}. Exportera igen från Circle K med{' '}
              {missingColumns.length === 1 ? 'den' : 'dem'} ikryssad
              {missingColumns.length === 1 ? '' : 'e'}.
            </Note>
          )}
          {previewCall.isError && (
            <Note tone="warning">
              Granskningen misslyckades. Försök igen om en stund.
            </Note>
          )}
          {file.length > 0 && renderFilePreview()}
        </FormWrap>
      </>
    );
  };

  return (
    <Modal
      buttons={buttons}
      closeOnOutsideClick={!busy}
      onClose={busy ? undefined : onCancel}
      title={preview ? 'Granska uppladdningen' : 'Ladda upp CSV-fil'}
    >
      {renderBody()}
    </Modal>
  );
};

export default UploadCsvModal;
